import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  isSupportedLocale,
  loadMessageCatalog,
  loadedMessageCatalog,
  type Locale,
  type MessageCatalog,
  type MessageKey,
} from './messages';

const localeStorageKey = 'rw.locale';

export function detectLocale(): Locale {
  const storedLocale = localStorage.getItem(localeStorageKey);
  if (storedLocale && isSupportedLocale(storedLocale)) return storedLocale;
  return navigator.language.toLowerCase().startsWith('ru') ? 'ru' : 'en';
}

interface I18nContextValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: MessageKey) => string;
}

const I18nContext = createContext<I18nContextValue | undefined>(undefined);

export function I18nProvider({ children }: PropsWithChildren) {
  const initialLocale = useMemo(detectLocale, []);
  const [state, setState] = useState<{ catalog: MessageCatalog | null; locale: Locale }>(() => ({
    catalog: loadedMessageCatalog(initialLocale),
    locale: initialLocale,
  }));
  const requestedLocale = useRef(initialLocale);

  useEffect(() => {
    if (state.catalog) return;
    let active = true;
    void loadMessageCatalog(state.locale).then((catalog) => {
      if (active && requestedLocale.current === state.locale) {
        setState({ catalog, locale: state.locale });
      }
    });
    return () => {
      active = false;
    };
  }, [state.catalog, state.locale]);

  useEffect(() => {
    if (!state.catalog) return;
    localStorage.setItem(localeStorageKey, state.locale);
    document.documentElement.lang = state.locale;
  }, [state.catalog, state.locale]);

  const setLocale = useCallback((locale: Locale) => {
    requestedLocale.current = locale;
    void loadMessageCatalog(locale).then((catalog) => {
      if (requestedLocale.current === locale) setState({ catalog, locale });
    });
  }, []);

  const value = useMemo<I18nContextValue>(
    () => ({
      locale: state.locale,
      setLocale,
      t: (key) => state.catalog?.[key] ?? key,
    }),
    [setLocale, state.catalog, state.locale]
  );

  if (!state.catalog) return null;
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const context = useContext(I18nContext);
  if (!context) throw new Error('useI18n must be used inside I18nProvider');
  return context;
}
